<script setup lang="ts">
import { useTheme } from 'vuetify'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import initCore from '@core/initCore'
import { initConfigStore, useConfigStore } from '@core/stores/config'
import { hexToRgb } from '@core/utils/colorConverter'
import LoaderOverlay from '@/modules/feedback/components/LoaderOverlay.vue'
import ToastMessage from '@/modules/feedback/components/ToastMessage.vue'
import ConsentPdDialog from '@/modules/user/components/ConsentPdDialog.vue'
import OnboardingOverlay from '@/modules/onboarding/components/OnboardingOverlay.vue'
import { initGlobalChatSync, resetGlobalChatSync } from '@/composables/useChatModuleBridge'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import { useAuthStore } from '@/modules/authStore/stores/authStore'
import { ERouteNames } from '@/router/ERouteNames'

const { global } = useTheme()

const ChatMessageToast = defineAsyncComponent(() => import('@/views/apps/chat-matrix-fast/components/ChatMessageToast.vue'))
const BridgedSlidingChat = defineAsyncComponent(() => import('@/components/chat/BridgedSlidingChat.vue'))
const FormAdverseEvent = defineAsyncComponent(() => import('@/views/AdverseEvents/FormAdverseEvent.vue'))
const TicketForm = defineAsyncComponent(() => import('@/modules/tickets/components/TicketForm.vue'))
const AuditNonconformityForm = defineAsyncComponent(() => import('@/modules/audits/stores/useAuditNonconformityFormStore/components/AuditNonconformityForm.vue'))
const ImprovementFormModal = defineAsyncComponent(() => import('@/modules/improvements/components/ImprovementFormModal.vue'))
const CitizenInquiryFormModal = defineAsyncComponent(() => import('@/modules/citizenInquiries/components/CitizenInquiryFormModal.vue'))
const StandaloneTaskExecutorModal = defineAsyncComponent(() => import('@/modules/tasks/components/StandaloneTaskExecutorModal.vue'))

// ℹ️ Sync current theme with initial loader theme
initCore()
initConfigStore()

const configStore = useConfigStore()
const feedbackStore = useFeedbackStore()
const userStore = useUserStore()
const authStore = useAuthStore()
const route = useRoute()
const isMessengerRoute = computed(() => route.name === ERouteNames.CHAT_MATRIX)

// Защита от F12: при смене роута увеличиваем consentKey, что форсит перерисовку диалога
const consentKey = ref(0)
const noConsent = computed(() => authStore.isAuthenticated && userStore.user && !userStore.user.is_terms_accepted)

watch(() => route.path, () => noConsent && consentKey.value++)

watch(
  () => authStore.isAuthenticated,
  isAuthenticated => {
    if (isAuthenticated) {
      initGlobalChatSync()
    } else {
      resetGlobalChatSync()
    }
  },
  { immediate: true },
)
</script>

<template>
  <LoaderOverlay />
  <ToastMessage />
  <ChatMessageToast />
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary (and yes, you really need to hexToRgb() that) -->
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`">
      <RouterView />

      <ScrollToTop v-if="route.name !== ERouteNames.IMPORT_DATA" />
      <BridgedSlidingChat v-if="!isMessengerRoute" />

      <!-- Глобальные диалоги для форм НС и тикетов при открытии из уведомлений -->
      <FormAdverseEvent
        v-if="feedbackStore.globalAdverseEventId !== null"
        :event-id="feedbackStore.globalAdverseEventId"
        is-global
      />
      <TicketForm
        v-if="feedbackStore.globalTicketId !== null"
        :key="feedbackStore.globalTicketId"
        :ticket-id="feedbackStore.globalTicketId"
        is-global
      />
      <AuditNonconformityForm
        v-if="feedbackStore.globalAuditNonconformityReportId !== null"
        :key="feedbackStore.globalAuditNonconformityReportId"
        :report-id="feedbackStore.globalAuditNonconformityReportId"
        :audit-id="feedbackStore.globalAuditNonconformityAuditId || undefined"
        :initial-tab="feedbackStore.globalFormTab ?? undefined"
        :is-global="true"
      />
      <ImprovementFormModal
        v-if="feedbackStore.globalImprovementId !== null"
        :suggestion-id="feedbackStore.globalImprovementId"
        is-global
      />
      <CitizenInquiryFormModal
        v-if="feedbackStore.globalCitizenInquiryId !== null"
        :appeal-id="feedbackStore.globalCitizenInquiryId"
        is-global
      />
      <StandaloneTaskExecutorModal
        v-if="feedbackStore.globalResponsibilityEntryId !== null"
        :entry-id="feedbackStore.globalResponsibilityEntryId"
      />

      <!-- Блокирующий диалог Согласия на обработку ПД -->
      <ConsentPdDialog v-if="noConsent" :key="consentKey" />

      <OnboardingOverlay v-if="authStore.isAuthenticated" />
    </VApp>
  </VLocaleProvider>
</template>
